import { XStack, YStack } from 'tamagui';
import { LinearGradient } from 'tamagui/linear-gradient';

import {
    type ColorTokenKey,
    useThemeColors,
} from '../../themes/SmoothieColorTokens';
import { hitSlop } from '../componentHelpers';
import { Icon, type IconType } from '../icons/Icon';
import { LabelRegular } from '../text/Typography';

export type BadgeLabelVariant =
    | 'neutral'
    // for use on a gray ($gray200) surface, where 'neutral' would blend in
    | 'neutral-alt'
    | 'positive'
    | 'warning'
    | 'danger'
    // non-semantic colors, e.g. for data viz
    | 'green'
    | 'blue'
    | 'marigold'
    | 'gray'
    | 'premium';

export type BadgeActionVariant = 'neutral' | 'primary' | 'warning' | 'danger';

const LabelTextColor: Record<BadgeLabelVariant, ColorTokenKey> = {
    neutral: '$gray1100',
    'neutral-alt': '$gray1100',
    positive: '$green1100',
    warning: '$orange1100',
    danger: '$red1100',
    green: '$green1100',
    blue: '$dropletBlue1100',
    marigold: '$marigold1100',
    gray: '$gray1100',
    premium: '$peach1100',
};

const LabelBackgroundColor: Record<BadgeLabelVariant, ColorTokenKey> = {
    neutral: '$gray200',
    'neutral-alt': '$gray400',
    positive: '$green300',
    warning: '$orange300',
    danger: '$red300',
    green: '$green300',
    blue: '$dropletBlue300',
    marigold: '$marigold300',
    gray: '$gray300',
    premium: '$peachLight',
};

const ActionTextColor: Record<BadgeActionVariant, ColorTokenKey> = {
    neutral: '$gray1100',
    primary: '$white',
    warning: '$orange1100',
    danger: '$red1100',
};

const ActionBackgroundColor: Record<BadgeActionVariant, ColorTokenKey> = {
    neutral: '$gray200',
    primary: '$black',
    warning: '$orange300',
    danger: '$red200',
};

const disabledTextColor: ColorTokenKey = '$gray800';
const disabledBackgroundColor: ColorTokenKey = '$gray200';

const PremiumBadgeGradient = () => {
    const { getColor } = useThemeColors();

    return (
        <YStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={0}
        >
            <LinearGradient
                colors={[getColor('$peachLight'), getColor('$peach')]}
                locations={[0.1479, 0.9608]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                width="100%"
                height="100%"
            />
        </YStack>
    );
};

export interface BadgeLabelProps {
    variant: BadgeLabelVariant;
    text: string;
    icon?: IconType;
    size?: 'regular' | 'large';
}

interface BadgeActionProps {
    variant: BadgeActionVariant;
    text: string;
    icon?: IconType;
    onPress?: () => void;
    disabled?: boolean;
}

export const BadgeLabel = ({
    variant,
    text,
    icon,
    size = 'large',
}: BadgeLabelProps) => {
    const paddingHorizontal = size === 'large' ? 14 : 8;
    const paddingVertical = size === 'large' ? 8 : 4;

    return (
        <XStack
            paddingHorizontal={paddingHorizontal}
            paddingVertical={paddingVertical}
            borderRadius={999}
            backgroundColor={LabelBackgroundColor[variant]}
            alignItems="center"
            width="fit-content"
            position="relative"
            overflow="hidden"
        >
            {variant === 'premium' && <PremiumBadgeGradient />}
            <XStack zIndex={1} gap={4} alignItems="center">
                {icon && (
                    <Icon
                        icon={icon}
                        size={12}
                        color={LabelTextColor[variant]}
                    />
                )}
                <LabelRegular color={LabelTextColor[variant]}>
                    {text}
                </LabelRegular>
            </XStack>
        </XStack>
    );
};

export const BadgeAction = ({
    variant,
    text,
    icon,
    onPress,
    disabled,
}: BadgeActionProps) => {
    const textColor = disabled ? disabledTextColor : ActionTextColor[variant];
    const backgroundColor = disabled
        ? disabledBackgroundColor
        : ActionBackgroundColor[variant];
    return (
        <XStack
            paddingHorizontal={14}
            paddingVertical={8}
            gap={4}
            borderRadius={999}
            backgroundColor={backgroundColor}
            alignItems="center"
            width="auto"
            onPress={disabled ? undefined : onPress}
            cursor={disabled ? 'default' : 'pointer'}
            {...(!disabled && {
                pressStyle: {
                    scale: 0.96,
                    opacity: 0.87,
                },
            })}
            animation="button"
            animateOnly={['transform', 'opacity']}
            {...hitSlop(4)}
        >
            {icon && <Icon icon={icon} size={12} color={textColor} />}
            <LabelRegular color={textColor}>{text}</LabelRegular>
        </XStack>
    );
};
